import { useEffect, useId, useMemo } from "react";
import { useIcps } from "../hooks/useIcps";

interface Props {
  id?: string;
  value: string;
  onChange: (next: string) => void;
  /** ICP currently selected in the surrounding form. When set, surfaces only
   * that ICP's configured campaign in the dropdown; free-text remains allowed. */
  contextIcp?: string;
  placeholder?: string;
  required?: boolean;
}

interface Suggestion {
  id: string;
  label: string;
}

/**
 * Campaign ID input with a native `<datalist>` of suggestions read from the
 * ``icps`` table (one entry per ICP that has ``lemlist_campaign_id`` set).
 * Users can pick from the list or type any value.
 */
export function CampaignInput({
  id,
  value,
  onChange,
  contextIcp,
  placeholder = "cam_...",
  required,
}: Props) {
  const fallbackId = useId();
  const inputId = id ?? fallbackId;
  const listId = `${inputId}-options`;
  const { data: icps } = useIcps();

  const suggestions = useMemo<Suggestion[]>(() => {
    if (!icps) return [];
    return icps
      .filter((icp) => icp.lemlist_campaign_id)
      .filter((icp) => !contextIcp || icp.name === contextIcp)
      .map((icp) => ({
        id: icp.lemlist_campaign_id as string,
        label: `default ${icp.display_name} Campaign ID`,
      }));
  }, [icps, contextIcp]);

  // Auto-fill the field with the selected ICP's configured campaign on mount
  // and whenever the ICP selection changes. Manual edits stick until the ICP
  // changes again.
  useEffect(() => {
    if (!contextIcp || !icps) return;
    const match = icps.find(
      (icp) => icp.name === contextIcp && icp.lemlist_campaign_id,
    );
    if (match?.lemlist_campaign_id && match.lemlist_campaign_id !== value) {
      onChange(match.lemlist_campaign_id);
    }
  }, [contextIcp, icps]);

  return (
    <>
      <input
        type="text"
        id={inputId}
        list={listId}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value.trim())}
        required={required}
        autoComplete="off"
      />
      {suggestions.length > 0 && (
        <datalist id={listId}>
          {suggestions.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </datalist>
      )}
    </>
  );
}
